import { BaseTable } from '@sede-enterprise/shell-ds-react-framework';
import styled, { createGlobalStyle } from 'styled-components';

// Styles to prevent body scroll when the Returns confirmation modal is open
export const GlobalModalOpenStyles = createGlobalStyle`
  body.returns-modal-open {
    overflow: hidden !important;
    padding-right: 0 !important;
  }
`;

// Global styles for column widths in the table
export const ColumnWidthStyles = createGlobalStyle`
  /* Force table to use our column widths */
  .shell-ds-table th:nth-child(1),
  .shell-ds-table td:nth-child(1) {
    min-width: 220px !important;
    max-width: 220px !important;
    width: 220px !important;
  }
  
  .shell-ds-table th:nth-child(2),
  .shell-ds-table td:nth-child(2) {
    min-width: 180px !important;
    max-width: 180px !important;
    width: 180px !important;
  }
  
  .shell-ds-table th:nth-child(3),
  .shell-ds-table td:nth-child(3) {
    min-width: 160px !important;
    max-width: 160px !important;
    width: 160px !important;
  }
  
  .shell-ds-table th:nth-child(4),
  .shell-ds-table td:nth-child(4) {
    min-width: 100px !important;
    max-width: 100px !important;
    width: 100px !important;
  }
  
  .shell-ds-table th:nth-child(5),
  .shell-ds-table td:nth-child(5) {
    min-width: 130px !important;
    max-width: 130px !important;
    width: 130px !important;
  }
  
  .shell-ds-table th:nth-child(6),
  .shell-ds-table td:nth-child(6) {
    min-width: 100px !important;
    max-width: 100px !important;
    width: 100px !important;
  }
  
  /* Aggressively remove all scrollbars from Returns panel and its contents */
  [data-testid="ReturnsPanel"] *,
  [data-testid="ReturnsPanel"] *::-webkit-scrollbar {
    overflow: hidden !important;
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE/Edge */
  }
  
  /* Hide webkit scrollbars */
  [data-testid="ReturnsPanel"] *::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  
  /* Target shell table specifically */
  .shell-ds-table,
  .shell-ds-table-wrapper,
  .shell-ds-table-container,
  .shell-ds-table-body {
    overflow: hidden !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }
`;

// Global styles for modal z-index and positioning
export const ModalStyles = createGlobalStyle`
  /* Prevent body scrollbars when modal is open */
  body:has(div[role="dialog"]) {
    overflow: visible !important;
    padding-right: 0 !important; /* Prevent scrollbar width compensation */
  }

  /* Target all modal elements and their wrappers */
  .shell-ds-modal-backdrop,
  .shell-ds-modal-overlay,
  .shell-ds-modal-wrapper,
  div[role="dialog"],
  .shell-ds-modal {
    z-index: 9999 !important;
    position: fixed !important;
  }
  
  /* Position modal at the top instead of center to prevent scrollbars */
  div[role="dialog"] {
    top: 10% !important; /* Position near the top of viewport */
    left: 50% !important;
    transform: translateX(-50%) !important; /* Only horizontal centering */
    margin: 0 !important;
    position: fixed !important;
    width: auto !important;
    height: auto !important;
    min-width: 400px !important;
    max-width: min(600px, 90vw) !important;
    max-height: min(500px, 80vh) !important;
    overflow-y: visible !important;
  }
  
  /* Ensure modal content fits properly */
  .shell-ds-modal-content,
  .shell-ds-modal-body {
    overflow: visible !important;
    padding: 20px !important;
    max-height: none !important;
  }
  
  /* Ensure backdrop is properly positioned and doesn't affect layout */
  .shell-ds-modal-backdrop {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background-color: rgba(0, 0, 0, 0.5) !important;
    z-index: 9998 !important;
  }
  
  /* Force Returns panel to maintain its size when modal opens */
  [data-testid="ReturnsPanel"] {
    overflow: hidden !important;
    position: fixed !important;
  }

  /* Allow shell modal wrapper to overflow content instead of clipping */
  .shell-modal-container-wrap {
    overflow: visible !important;
  }
`;

export const ReturnsPanelStyle = styled.div<{ dynamicHeight?: number; disabled?: boolean }>`
  background: ${(prop) => prop.theme.background.surface}; 
  padding: 15px;
  z-index: 10;
  box-shadow: -4px 0px 10px rgba(0, 0, 0, 0.2);
  position: fixed;
  height: ${props => props.dynamicHeight ? `${props.dynamicHeight}px` : '500px'}; /* Set fixed fallback height */
  min-height: 300px; /* Minimum height for very few rows */
  max-height: 700px; /* Maximum height for many rows */
  overflow: hidden !important; /* Force no scrollbars with !important */
  width: 1100px;
  max-width: 100%; /* Ensures it doesn't overflow on very small screens */
  display: flex;
  flex-direction: column; 
  bottom: 0; /* Position at the bottom */
  right: 0; /* Position at the right side */
  border-top: 3px solid #fcd839; /* Top yellow border for emphasis */
  border-left: 3px solid #fcd839; /* Left yellow border for emphasis */
  transform: none !important; /* Prevent any transform adjustments */
  transition: opacity 0.2s ease, height 0.3s ease; /* Animate opacity and height */

  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0.8;
  `}
  
  /* Ensure all child elements also don't overflow */
  * {
    overflow: hidden !important;
  }
`;

export const ReturnsHeader = styled.div`
  display: flex;
  height: 40px;
  margin-left: 0.5rem;
  margin-bottom: 5px; /* Reduced margin */
  align-items: center; /* Center align items vertically */
  .crossIcon {
    margin-left: auto;
    cursor: pointer;
    height: 25px;
    width: 25px;
  }  
`;

export const ReturnsRowContainer = styled.div`
  display: flex;
  color: white;
  height:18px;
  flex-direction: row;
  flex-wrap: no wrap;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  margin-top: 10px;
`;

export const ReturnsText = styled.div`
  width: 160px;
  height:18px;
  display: flex;  
`;

export const ReturnsText2 = styled.div` 
  height:15px;
  display: flex;
  margin-top: 1px;
  margin-left: 1px;
  align-items: left;
`;

export const TableContainer = styled.div<{ dynamicHeight?: number }>`
  margin: 16px;
  flex: 1;
  height: ${props => props.dynamicHeight ? `${props.dynamicHeight}px` : 'calc(40vh - 180px)'};
  background: ${({ theme }) => theme.background};
  overflow: hidden; /* Remove scrollbars from table container too */
  max-width: 1070px; /* Set maximum width to prevent expansion - reduced by 90px */
  width: 1070px; /* Fixed width to match the original panel width - reduced by 90px */
  margin-right: auto; /* Center the table */
  margin-left: 0; /* Align to the left */
`;

export const TableStyled = styled(BaseTable)`
  width: 100%;
  table-layout: fixed; /* This should respect column widths */
  
  th, td {
    padding: 8px 8px; /* Reduced horizontal padding */
    white-space: nowrap;
    background: ${({ theme }) => theme.background};
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  /* Force table to respect column widths */
  th, td {
    width: auto !important; /* Override any predefined widths */
  }
  
  /* Apply width constraints to individual column headers/cells */
  th:nth-child(1), td:nth-child(1) {
    width: 220px !important; /* Source Auction */
  }
  
  th:nth-child(2), td:nth-child(2) {
    width: 180px !important; /* Delivery From/To */
  }
  
  th:nth-child(3), td:nth-child(3) {
    width: 160px !important; /* Capacity */
  }
  
  th:nth-child(4), td:nth-child(4) {
    width: 100px !important; /* Clearing Price */
  }
  
  th:nth-child(5), td:nth-child(5) {
    width: 130px !important; /* Quantity to return */
  }
  
  th:nth-child(6), td:nth-child(6) {
    width: 100px !important; /* Edit/Cancel Button */
    text-align: center;
  }

  thead tr {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${({ theme }) => theme.background};
  }
`;

export const EditButtonCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

export const EditButton = styled.button<{ isCancel?: boolean }>`
  padding: 4px 8px;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  width: 80px;
  border: 1px solid ${props => props.isCancel ? '#d9534f' : '#5bc0de'};
  background: ${props => props.isCancel ? 'rgba(217, 83, 79, 0.2)' : 'rgba(91, 192, 222, 0.2)'};
  color: ${props => props.isCancel ? '#d9534f' : '#5bc0de'};
  
  &:hover {
    background: ${props => props.isCancel ? 'rgba(217, 83, 79, 0.3)' : 'rgba(91, 192, 222, 0.3)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

export const ActionButton = styled.button<{ color: 'green' | 'red' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid ${props => props.color === 'green' ? '#5cb85c' : '#d9534f'};
  background: ${props => props.color === 'green' ? 'rgba(92, 184, 92, 0.2)' : 'rgba(217, 83, 79, 0.2)'};
  color: ${props => props.color === 'green' ? '#5cb85c' : '#d9534f'};
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  
  &:hover {
    background: ${props => props.color === 'green' ? 'rgba(92, 184, 92, 0.3)' : 'rgba(217, 83, 79, 0.3)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ReturnsFooter = styled.div`
  height: auto;
  padding: 10px 0; /* Reduced padding from 20px to 10px */
  display: flex;
  justify-content: flex-end; /* Keep alignment to the right */
  margin-right: 16px;
  position: relative; /* Added for positioning the right side container */
`;

export const FooterContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end; /* Right align all content */
  gap: 10px; /* Space between elements */
  width: 210px; /* Fixed width to match the button width */
  margin-right: 15px; /* Add right margin for spacing from panel edge */
`;

export const FooterSpacer = styled.div`
  height: 15px; /* Add additional space above the footer content */
  width: 100%;
`;

export const BookSelectionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 16px 12px 16px; /* Added margin on left/right sides and bottom */
  padding: 5px 0; /* Added some padding to top/bottom */
`;

export const BookLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  margin-right: 4px;
`;

export const BookSelect = styled.select`
  padding: 6px 10px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid #ffffff;
  background-color: #1e242b;
  color: #ffffff;
  min-width: 150px;
  cursor: pointer;
  
  option {
    background-color: #1e242b;
    color: #ffffff;
  }
  
  &:focus {
    outline: none;
    border-color: #5bc0de;
    box-shadow: 0 0 0 2px rgba(91, 192, 222, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export const RightSideContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end; /* Right align the contents */
  width: 250px; /* Set fixed width for the container */
  margin-left: 160px; /* Create space on the left */
`;

export const ButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;
